/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Mocks } from '@core/services/mocks/mocks';
import { delay, finalize } from 'rxjs/operators';
import dayjs from 'dayjs';
import { InitFlow } from '@core/models/swagger/initFlow';
import { Patterns } from '@global/enums';

@Injectable({
  providedIn: 'root',
})
export class InitFlowService extends ApiService {
  mocked = false;

  private readonly loadingSubjects: Record<string, BehaviorSubject<boolean>> = {
    postInitFlow: new BehaviorSubject<boolean>(false),
  };

  readonly postInitFlowLoading$ = this.loadingSubjects.postInitFlow.asObservable();

  private executePostRequest<TRequest, TResponse>({
    endpoint,
    body,
    requestKey,
  }: {
    endpoint: string;
    body: TRequest;
    requestKey: string;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    return this.post<TRequest, TResponse>({ suffix: endpoint, body }).pipe(
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }

  postInitFlow(body?: InitFlowBody): Observable<InitFlow> {
    body = this.createInitFlowBody(body);
    return this.executePostRequest<InitFlowBody, InitFlow>({
      endpoint: '/initflow',
      body,
      requestKey: 'postInitFlow',
    });
  }

  private createInitFlowBody(body?: InitFlowBody): InitFlowBody {
    return {
      ratingDate: dayjs().format(Patterns.DATE_DASH_YYYYMMDD),
      ...body,
      metadata: {
        date: dayjs().format(Patterns.DATE_DASH_YYYYMMDD),
        browser: this.getUserBrowser(),
        locale: 'en',
        testKind: null,
        time: new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hour12: true,
        }).format(new Date()),
      },
    };
  }

  private getUserBrowser(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) {
      return 'Chrome';
    } else if (userAgent.includes('Firefox')) {
      return 'Firefox';
    } else if (userAgent.includes('Safari')) {
      return 'Safari';
    } else if (userAgent.includes('Edge')) {
      return 'Edge';
    } else {
      return 'Unknown';
    }
  }
}

interface InitFlowBody {
  ratingDate?: string;
  metadata?: any;
  campaignCode?: string;
  promotionCode?: string;
}
